<template>
  <div>
    <MfaLoginModal />
    <AuthWrapper>
      <template #header>
        <h1
          data-test-id="login-title"
          class="text-2xl [&_strong]:font-medium [&_strong]:text-primary"
          v-html="t('auth.login.title', { appTitle: whiteLabelRef.companyTitle })"
        ></h1>
      </template>
      <template #body>
        <Card>
          <CardContent>
            <form ref="formRef" class="flex flex-col gap-6" @submit.prevent="onSubmit">
              <FormField
                v-slot="{ componentField }"
                name="email"
                :validate-on-model-update="false"
                :validate-on-blur="false"
              >
                <!-- Masqué mais monté à l'étape 2 : vee-validate garde la valeur, et
                     les gestionnaires de mots de passe y lisent l'identifiant
                     (autocomplete="username") pour rattacher le mot de passe. -->
                <FormItem v-show="!isMethodStepRef">
                  <FormLabel>{{ t('ui.input.email.label') }}</FormLabel>
                  <InputGroup>
                    <FormControl>
                      <InputGroupInput
                        type="email"
                        autocomplete="username"
                        data-test-id="login-email_input-email"
                        :placeholder="t('auth.login.email.placeholder')"
                        :aria-busy="showSpinnerRef"
                        v-bind="componentField"
                      />
                    </FormControl>
                    <InputGroupAddon v-if="showSpinnerRef" align="inline-end">
                      <Spinner />
                    </InputGroupAddon>
                  </InputGroup>
                  <FormMessage />
                </FormItem>
              </FormField>

              <!-- Étape 2 : l'adresse résolue, figée, avec de quoi la changer. -->
              <div
                v-if="isMethodStepRef"
                data-test-id="login-step2_identity"
                class="flex items-center justify-between gap-3 rounded-md bg-muted py-2.5 pr-2 pl-4"
              >
                <div class="flex min-w-0 flex-col gap-0.5">
                  <span class="text-xs text-muted-foreground">{{ t('auth.login.signedInAs') }}</span>
                  <span class="truncate text-sm font-semibold">{{ (values.email ?? '').trim() }}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-test-id="login-step2_back-to-email"
                  data-step2-back
                  :aria-label="t('auth.login.changeEmailAriaLabel')"
                  @click="backToEmailStep"
                >
                  {{ t('auth.login.changeEmail') }}
                </Button>
              </div>

              <!-- Étape 2 : une seule méthode, celle qui marche pour cette adresse. -->
              <template v-if="isMethodStepRef">
                <div v-if="showPasswordFieldRef" class="flex flex-col gap-2">
                  <FormField
                    v-slot="{ componentField }"
                    name="password"
                    :validate-on-model-update="false"
                    :validate-on-blur="false"
                  >
                    <FormItem>
                      <FormLabel>{{ t('ui.input.password.label') }}</FormLabel>
                      <InputGroup>
                        <FormControl>
                          <InputGroupInput
                            :type="showPasswordRef ? 'text' : 'password'"
                            autocomplete="current-password"
                            data-step2-focus
                            data-test-id="login-email_input-password"
                            v-bind="componentField"
                          />
                        </FormControl>
                        <InputGroupAddon align="inline-end">
                          <InputGroupButton
                            type="button"
                            size="icon-xs"
                            :aria-label="showPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                            @click="showPasswordRef = !showPasswordRef"
                          >
                            <EyeOffIcon v-if="showPasswordRef" />
                            <EyeIcon v-else />
                          </InputGroupButton>
                        </InputGroupAddon>
                      </InputGroup>
                      <FormMessage />
                    </FormItem>
                  </FormField>
                  <!-- Un seul lien pour l'oubli et la première connexion : une
                       invitation jamais activée passe par la même
                       réinitialisation, qui pose le premier mot de passe, et
                       l'API ne dit pas lequel des deux cas s'applique. -->
                  <router-link
                    data-test-id="login-email_link-forgot-password"
                    :to="forgotPasswordRouteRef"
                    class="text-sm font-medium !text-primary !no-underline hover:!underline"
                    @click="trackPasswordResetClick"
                    >{{ t('auth.login.forgotPassword') }}</router-link
                  >
                </div>

                <Alert
                  v-else-if="resolvedMethodRef === 'not_found'"
                  variant="destructive"
                  data-test-id="login-step2_alert-not-found"
                >
                  <AlertTitle>{{ t('auth.login.unknownEmail.title') }}</AlertTitle>
                  <AlertDescription>{{ t('auth.login.unknownEmail.body') }}</AlertDescription>
                </Alert>
              </template>

              <div class="mt-2 flex flex-col gap-4">
                <Button v-if="!isMethodStepRef" type="submit" data-test-id="login-step1_submit-continue" class="w-full">
                  {{ t('auth.login.continue') }}
                </Button>
                <Button
                  v-else-if="showSsoButtonRef"
                  as="a"
                  :href="ROUTES.LOGIN_WITH_SAML"
                  variant="outline"
                  data-test-id="login-step2_sso-button"
                  data-step2-focus
                  class="w-full !text-foreground !no-underline"
                  @click="trackSsoRedirect"
                >
                  <img
                    v-if="whiteLabelRef.logoSSO || whiteLabelRef.logoSmall"
                    :src="whiteLabelRef.logoSSO || whiteLabelRef.logoSmall"
                    alt=""
                    class="!h-4 !w-4 shrink-0 rounded-[5px] !object-contain"
                  />
                  {{ t('auth.login.continueWith', { companyName: whiteLabelRef.companyName }) }}
                </Button>
                <Button
                  v-else-if="showPasswordFieldRef"
                  type="submit"
                  data-test-id="login-email_submit-connect-email"
                  class="w-full"
                >
                  {{ t('auth.login.login') }}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </template>
    </AuthWrapper>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { EyeIcon, EyeOffIcon } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { z } from 'zod'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { Spinner } from '@/components/ui/spinner'
import { fetchLoginMethod, login } from '@/features/auth/api'
import AuthWrapper from '@/features/auth/components/auth-wrapper/auth-wrapper.vue'
import MfaLoginModal from '@/features/auth/components/mfa-login-modal/mfa-login-modal.vue'
import useLoginSession from '@/features/auth/hooks/use-login-session.hook'
import { captureReturnTo } from '@/features/auth/hooks/use-return-to.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import type { LoginMethodType } from '@/features/auth/types'
import programsStore from '@/features/programs/stores/programs/programs.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import { EMAIL_REGEX, ROUTES } from '@/utils/config/constants'
import { debounce } from '@/utils/math/debounce'
import sleep from '@/utils/misc/sleep'

/**
 * Connexion en deux temps : l'adresse d'abord, puis la seule méthode qui marche
 * pour cette adresse.
 *
 * Avant, la page demandait l'adresse et le mot de passe ensemble et affichait en
 * plus le bouton SSO de l'établissement quelle que soit l'adresse saisie. Deux
 * conséquences : une personne inscrite avec une adresse personnelle cliquait sur
 * le SSO et se faisait refuser, et une adresse inexistante recevait
 * « identifiant ou mot de passe incorrect », donc cherchait son mot de passe
 * alors que c'est son adresse qui était fausse.
 */

const { whiteLabelRef } = useWhiteLabel()

const { t } = useI18n()
const router = useRouter()

// Plateforme vierge (aucun compte) : le premier visiteur la crée sur /setup.
watch(
  () => whiteLabelRef.value.setupNeedsFirstAdmin,
  (needsFirstAdmin) => {
    if (needsFirstAdmin) router?.replace(ROUTES.CREATE_PLATFORM).catch(() => {})
  },
  { immediate: true },
)

type StepType = 'email' | 'method'

const stepRef = ref<StepType>('email')
const formRef = ref<HTMLFormElement | null>(null)
const resolvedMethodRef = ref<LoginMethodType | undefined>(undefined)
const showPasswordRef = ref(false)

const isMethodStepRef = computed(() => stepRef.value === 'method')

/**
 * Le SSO n'est proposé que si la plateforme l'a activé. Un compte rattaché à une
 * identité SSO sur une plateforme où le SAML a été coupé retombe sur le mot de
 * passe, et sur « créez votre mot de passe » s'il n'en a pas.
 */
const showSsoButtonRef = computed(
  () => resolvedMethodRef.value === 'sso' && whiteLabelRef.value?.samlLoginEnabled === 'true',
)

const showPasswordFieldRef = computed(
  () => isMethodStepRef.value && !showSsoButtonRef.value && resolvedMethodRef.value !== 'not_found',
)

// KC-439 : session de connexion partagée (timings, tentatives, deep-link)
const loginSession = useLoginSession()

onMounted(() => {
  loginSession.start()
  // KC-501 / KC-585 : préserve le deep-link d'entrée (?programId=&tab=&utm_*)
  // avant que l'authentification (email, MFA ou SSO) ne fasse perdre la query.
  captureReturnTo()
  trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_START, {
    has_sso_option: whiteLabelRef.value?.samlLoginEnabled === 'true',
    program_slug: whiteLabelRef.value?.companyName ?? null,
    had_program_deep_link: loginSession.hadProgramDeepLink(),
  })
})

// Bilan UX d'un login email réussi (le login MFA est tracké dans la modale)
function trackEmailLoginComplete(isFirstLogin: boolean) {
  trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_COMPLETE, {
    auth_method: 'email',
    time_to_login_ms: loginSession.timeSinceStart(),
    failed_attempts_in_session: Math.max(0, loginSession.attemptCount.value - 1),
    used_password_reset: loginSession.usedPasswordReset.value,
    is_first_login: isFirstLogin,
    program_id: programsStore.selectedProgramId,
    role: null,
    had_program_deep_link: loginSession.hadProgramDeepLink(),
    program_context_preserved: loginSession.programContextPreserved(),
  })
  loginSession.reset()
}

function trackSsoRedirect() {
  trackEvent(ANALYTICS_EVENTS.AUTH_SSO_REDIRECT, {
    program_slug: whiteLabelRef.value?.companyName ?? null,
    time_since_start_ms: loginSession.timeSinceStart(),
  })
}

function trackPasswordResetClick() {
  loginSession.markUsedPasswordReset()
  trackEvent(ANALYTICS_EVENTS.AUTH_PASSWORD_RESET_CLICK, {
    failed_attempts_before: loginSession.attemptCount.value,
    time_since_start_ms: loginSession.timeSinceStart(),
  })
}

// Le mot de passe n'est requis qu'à l'étape 2 : à l'étape 1 le formulaire ne
// valide que l'adresse.
const validationSchemaRef = computed(() =>
  toTypedSchema(
    z.object({
      email: z
        .string({ required_error: t('error.requiredEmailField') })
        .min(1, t('error.requiredEmailField'))
        .regex(EMAIL_REGEX, t('auth.login.email.errors.invalid')),
      password: showPasswordFieldRef.value
        ? z
            .string({ required_error: t('auth.password.errors.required') })
            .min(1, t('auth.password.errors.required'))
            .min(3, t('auth.login.password.errors.invalid'))
        : z.string().optional(),
    }),
  ),
)

const { handleSubmit, values, setFieldError } = useForm({
  validationSchema: validationSchemaRef,
  initialValues: { email: '', password: '' },
})

const forgotPasswordRouteRef = computed(
  () => `${ROUTES.FORGOT_PASSWORD()}${values.email ? `?email=${encodeURIComponent(values.email)}` : ''}`,
)

const debouncedValidateEmail = debounce(() => {
  const value = (values.email ?? '').trim()
  if (value && !EMAIL_REGEX.test(value)) {
    setFieldError('email', t('auth.login.email.errors.invalid'))
  }
}, 1000)

const debouncedValidatePassword = debounce(() => {
  const value = values.password ?? ''
  if (value && value.length < 3) {
    setFieldError('password', t('auth.login.password.errors.invalid'))
  }
}, 1000)

const { mutateAsync: resolveLoginMethod, isPending: isResolvingMethodRef } = useMutation({
  mutationFn: fetchLoginMethod,
})

// Anti-flash : n'affiche le spinner que si la requête dure plus de 250ms
const showSpinnerRef = ref(false)
let spinnerTimeoutId: ReturnType<typeof setTimeout> | undefined
watch(isResolvingMethodRef, (pending) => {
  if (pending) {
    spinnerTimeoutId = setTimeout(() => {
      showSpinnerRef.value = true
    }, 250)
  } else {
    if (spinnerTimeoutId) clearTimeout(spinnerTimeoutId)
    showSpinnerRef.value = false
  }
})

watch(
  () => values.email,
  () => {
    setFieldError('email', undefined)
    debouncedValidateEmail()
  },
)

watch(
  () => values.password,
  () => {
    setFieldError('password', undefined)
    debouncedValidatePassword()
    // marque que le mot de passe a été édité depuis la dernière tentative
    loginSession.markPasswordChanged()
  },
)

/**
 * Le champ ou le bouton qui avait le focus disparaît à chaque changement
 * d'étape : sans le replacer, le focus clavier retombe en haut de la page.
 * Étape 1 : l'adresse. Étape 2 : le mot de passe ou le bouton SSO, à défaut
 * « Changer » (écran « adresse inconnue »).
 */
async function focusCurrentStep() {
  await nextTick()
  const form = formRef.value
  if (!form) return
  const target = isMethodStepRef.value
    ? (form.querySelector<HTMLElement>('[data-step2-focus]') ?? form.querySelector<HTMLElement>('[data-step2-back]'))
    : form.querySelector<HTMLElement>('input[type="email"]')
  target?.focus()
}

function backToEmailStep() {
  stepRef.value = 'email'
  resolvedMethodRef.value = undefined
  focusCurrentStep()
}

async function goToMethodStep(email: string) {
  try {
    const { method } = await resolveLoginMethod(email)
    resolvedMethodRef.value = method
  } catch {
    // La résolution est un aiguillage, pas une validation : si elle échoue, on
    // laisse la personne tenter le mot de passe plutôt que de la bloquer.
    resolvedMethodRef.value = 'password'
  }
  stepRef.value = 'method'
  focusCurrentStep()
  trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_METHOD_RESOLVED, {
    method: resolvedMethodRef.value ?? null,
    time_since_start_ms: loginSession.timeSinceStart(),
  })
}

const onSubmit = handleSubmit(async ({ email, password }) => {
  if (!isMethodStepRef.value) {
    await goToMethodStep(email.trim())
    return
  }

  // Écrans SSO et « adresse inconnue » : il n'y a pas de mot de passe à
  // envoyer. Rien ne doit partir, sinon l'API répond une erreur affichée comme
  // un mot de passe faux.
  if (!showPasswordFieldRef.value) return

  await sleep(10) // delay for password autofill to settle before submit

  const { attemptNumber, fieldChanged } = loginSession.registerAttempt(email.trim())

  try {
    const loginData = await login({ email: email.trim(), password: password ?? '' })

    if (loginData?.message === 'MFA_REQUIRED') {
      modalsStore.updateModalAttachedData('mfa-login', { email: email.trim() })
      modalsStore.toggleModal('mfa-login', true)
      return
    }

    if (loginData?.error) {
      toast.error(t('error.common'))
      throw new Error(loginData.error)
    }

    await authStore.setAuthToken(loginData.token)
    trackEmailLoginComplete(loginData.isFirstLogin ?? false)
    router?.push(ROUTES.REDIRECT).catch(() => {})
  } catch (error: any) {
    if (error?.message === 'MFA_REQUIRED') {
      modalsStore.updateModalAttachedData('mfa-login', { email: email.trim() })
      modalsStore.toggleModal('mfa-login', true)
      return
    }

    // error_type générique : l'API renvoie le même message pour
    // user_not_found / incorrect_password (anti-énumération v1.74)
    trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
      auth_method: 'email',
      error_type: error?.status === 429 ? 'rate_limited' : 'invalid_credentials',
      attempt_number: attemptNumber,
      time_since_start_ms: loginSession.timeSinceStart(),
      field_changed_since_last: fieldChanged,
    })

    toast.error(t('auth.login.error'))
  }
})
</script>
